import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { AttachableTable, AttachmentInsert, AttachmentPurpose, AttachmentRow } from '../types/supabase'
import type { DeepPartialNullable } from '../types/util'
import { PURGE } from 'redux-persist';
import { replaceOrInsert } from '../lib/util/replaceOrInsert'
import { redactObjectExceptKeys } from '../lib/util/redact'
import { onSyncDownComplete } from './actions/onSyncDownComplete';

export type AttachmentModel = {
  id: string
  blob_key: string
  created_at: string
  created_by_user_id?: string | null
  deleted_at?: string | null
  membership_id: string
  record_id: string
  table_name: AttachableTable
  updated_at: string
  /** Blank until Claude classifies the document, or the member names it. */
  purpose: AttachmentPurpose | null
}

type AttachmentInsertPayload = AttachmentModel

export type AttachmentsSliceState = {
  attachments: Array<AttachmentModel>

  lastLoadFromServer?: number
}

const initialState: AttachmentsSliceState = {
  attachments: []
}

export const attachmentsSlice = createSlice({
  name: 'attachments',
  initialState,
  reducers: {
    createAttachment(state, action: PayloadAction<AttachmentInsertPayload>) {
      if (!state.attachments) { state.attachments = [] }
      const i = state.attachments.findIndex((a) => a.id === action.payload.id)
      if (i >= 0) {
        state.attachments[i] = action.payload
      } else {
        state.attachments.push(action.payload)
      }
    },
    updateAttachment(state, action: PayloadAction<Partial<AttachmentModel> & { id: string, updated_at: string }>) {
      if (!state.attachments) { state.attachments = [] }
      const i = state.attachments.findIndex((a) => a.id === action.payload.id)
      if (i >= 0) {
        state.attachments[i] = {
          ...state.attachments[i],
          ...action.payload,
        }
      }
    },
    deleteAttachment(state, action: PayloadAction<{ id: string, updated_at: string, deleted_at: string }>) {
      // Remove it out of the attachments array
      if (!state.attachments) { state.attachments = [] }
      const i = state.attachments.findIndex((e) => e.id === action.payload.id)
      if (i >= 0) {
        state.attachments.splice(i, 1)
      }
    },
  },
  extraReducers: (builder) => {
    builder = builder.addCase(PURGE, (state) => {
      return initialState
    }).addCase(onSyncDownComplete, (state, action) => {
      for (const attachment of action.payload.attachments?.updated || []) {
        replaceOrInsert(state.attachments, attachment)
      }
      for (const deletedAttachment of action.payload.attachments?.deleted || []) {
        const i = state.attachments.findIndex((t) => t.id === deletedAttachment.id)
        if (i >= 0 && state.attachments[i].updated_at <= deletedAttachment.updated_at) {
          state.attachments.splice(i, 1)
        }
      }
    })
  },
})

// Action creators are generated for each case reducer function
export const {
  createAttachment,
  updateAttachment,
  deleteAttachment
} = attachmentsSlice.actions

export type AttachmentsSliceAction = ReturnType<typeof attachmentsSlice.actions[keyof typeof attachmentsSlice.actions]>

export function isAttachmentsSliceAction(action: any): action is AttachmentsSliceAction {
  return action.type?.startsWith(attachmentsSlice.name)
}

export default attachmentsSlice.reducer

/*
 * Redact all sensitive information from the attachments, so that they can be sent to Analytics tools like
 * Sentry or Amplitude.
 * Due to HIPAA compliance, we cannot send any patient information to these tools.
 * The redaction operates with a whitelist to ensure that we don't accidentally send any sensitive information in the future.
*/

export function redactAttachments(state: AttachmentsSliceState): DeepPartialNullable<AttachmentsSliceState> {
  return {
    attachments: state.attachments?.map(redactAttachment)
  }
}

export function redactAttachment(attachment: Partial<AttachmentRow>): DeepPartialNullable<AttachmentRow> {
  return {
    ...redactObjectExceptKeys(attachment,
      'id', 'created_at', 'updated_at', 'created_by_user_id', 'membership_id', 'record_id', 'table_name', 'blob_key', 'deleted_at'),
  }
}

export function redactAttachmentsSliceAction(action: AttachmentsSliceAction) {
  switch(action.type) {
    case createAttachment.type:
    case updateAttachment.type:
    case deleteAttachment.type:
      return {
        type: action.type,
        payload: redactAttachment(action.payload)
      }

    default:
      return {
        type: (action as any).type,
        payload: 'REDACTED'
      }
  }
}

// Assert we can assign an AttachmentRow to an AttachmentModel
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertAttachmentRowAssignable: AttachmentModel = {} as AttachmentRow

// Assert we can assign an AttachmentModel to an AttachmentInsert
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertAttachmentRowAssignable2: AttachmentInsert = {} as AttachmentModel
